/**
 * K1 (2026-08-05) — Source-metadata info badge for atlas facts.
 *
 * Renders a tiny lucide `Info` icon that reveals a tooltip listing the
 * five knowledge-graph provenance fields on hover, focus, or tap. The
 * badge stays hidden entirely when every field is null/empty so
 * existing (pre-K1) facts render unchanged.
 *
 * Props: `fact` — object that MAY carry any subset of:
 *   - source_document       (string)
 *   - source_upload_date    (ISO date string)
 *   - source_supplier       (string)
 *   - confidence_score      (number, 0-1)
 *   - last_verified_at      (ISO date string)
 * `testId` — optional `data-testid` suffix for the trigger button.
 */
import React from "react";
import { Info } from "lucide-react";

const LABELS = [
  ["source_document",    "Source document"],
  ["source_supplier",    "Supplier"],
  ["source_upload_date", "Uploaded"],
  ["last_verified_at",   "Last verified"],
  ["confidence_score",   "Confidence"],
];

function formatValue(key, val) {
  if (val === null || val === undefined || val === "") return null;
  if (key === "confidence_score") {
    const n = Number(val);
    if (!Number.isFinite(n)) return null;
    return `${Math.round(n * 100)}%`;
  }
  if (key === "source_upload_date" || key === "last_verified_at") {
    // Best-effort humanization; falls back to raw string on parse fail.
    const d = new Date(val);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      });
    }
  }
  return String(val);
}

export function factHasSourceMetadata(fact) {
  if (!fact) return false;
  return LABELS.some(([k]) => {
    const v = fact[k];
    return v !== null && v !== undefined && v !== "";
  });
}

export default function FactSourceInfoBadge({ fact, testId }) {
  if (!factHasSourceMetadata(fact)) return null;
  const rows = LABELS
    .map(([k, label]) => [label, formatValue(k, fact[k]), k])
    .filter(([, v]) => v !== null);
  return (
    <span
      tabIndex={0}
      role="button"
      aria-label="Source metadata"
      data-testid={testId ? `fact-source-info-${testId}` : "fact-source-info"}
      className="relative inline-flex items-center justify-center h-5 w-5 rounded-full text-tb-navy/45 hover:text-tb-navy hover:bg-tb-navy/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-tb-navy/30 cursor-help group"
    >
      <Info className="w-3.5 h-3.5" aria-hidden />
      <span
        role="tooltip"
        data-testid={testId ? `fact-source-info-tooltip-${testId}` : "fact-source-info-tooltip"}
        className="pointer-events-none absolute bottom-full right-0 mb-1 w-64 rounded-lg border border-tb-navy/15 bg-white p-2.5 text-left shadow-lg opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0 transition z-50"
      >
        <span className="block text-[9px] uppercase tracking-widest font-hand text-tb-navy/50 mb-1">
          Source metadata
        </span>
        <span className="block space-y-0.5">
          {rows.map(([label, val, k]) => (
            <span key={k} className="flex items-baseline gap-2 text-[11px] font-hand text-tb-navy leading-snug">
              <span className="flex-shrink-0 text-tb-navy/60">{label}:</span>
              <span
                data-testid={testId ? `fact-source-info-${k}-${testId}` : `fact-source-info-${k}`}
                className="text-tb-navy break-words"
              >
                {val}
              </span>
            </span>
          ))}
        </span>
      </span>
    </span>
  );
}
